// ─────────────────────────────────────────────────────────────
//  HeatmapGrid  – Dept × Month spend utilization heatmap
//  Pure CSS + React – no extra lib needed
// ─────────────────────────────────────────────────────────────
import React, { useState } from "react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function getColor(pct) {
  if (pct === null || pct === undefined) return "#f5f5f5";
  if (pct > 110) return "#7b0d0d"; // over-budget dark red
  if (pct > 100) return "#c0392b"; // over budget
  if (pct > 90)  return "#e67e22"; // warning
  if (pct > 75)  return "#f1c40f"; // caution
  if (pct > 50)  return "#2ecc71"; // healthy
  if (pct > 25)  return "#a8e6cf"; // low
  return "#e8f8f0";                // very low
}

function getTextColor(pct) {
  if (pct > 90 || pct > 110) return "#fff";
  if (pct > 75) return "#333";
  return "#555";
}

const LEGEND = [
  { label: ">110% Over-budget", color: "#7b0d0d" },
  { label: "100–110% Over",     color: "#c0392b" },
  { label: "90–100% Warning",   color: "#e67e22" },
  { label: "75–90% Caution",    color: "#f1c40f" },
  { label: "50–75% Healthy",    color: "#2ecc71" },
  { label: "<50% Low",          color: "#a8e6cf" },
];

export default function HeatmapGrid({ data = [], title = "Department Spend Heatmap (% of Budget)" }) {
  const [tooltip, setTooltip] = useState(null);

  // Build dept → month → {spend, budget, pct}
  const depts = [...new Set(data.map((d) => d.department))].sort();
  const matrix = {};
  data.forEach(({ department, budget_month, spend, budget, utilization_pct }) => {
    if (!matrix[department]) matrix[department] = {};
    const mIdx = MONTHS.findIndex((m) => budget_month?.startsWith(m));
    const key  = mIdx >= 0 ? MONTHS[mIdx] : budget_month?.slice(0, 3);
    matrix[department][key] = { spend, budget, pct: parseFloat(utilization_pct) };
  });

  return (
    <div style={{
      background: "#fff", borderRadius: 12,
      boxShadow: "0 2px 12px rgba(73,105,171,0.08)",
      padding: "18px 16px", overflowX: "auto",
    }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 14 }}>{title}</div>

      <div style={{ position: "relative", display: "inline-block", minWidth: 700 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 3, fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ width: 140, textAlign: "left", padding: "4px 8px", color: "#888", fontWeight: 600 }}>Department</th>
              {MONTHS.map((m) => (
                <th key={m} style={{ width: 54, textAlign: "center", color: "#888", fontWeight: 600 }}>{m}</th>
              ))}
              <th style={{ width: 70, textAlign: "center", color: "#888", fontWeight: 600 }}>Avg %</th>
            </tr>
          </thead>
          <tbody>
            {depts.map((dept) => {
              const row  = matrix[dept] || {};
              const pcts = MONTHS.map((m) => row[m]?.pct).filter((v) => v !== undefined);
              const avg  = pcts.length ? (pcts.reduce((a, b) => a + b, 0) / pcts.length).toFixed(1) : null;
              return (
                <tr key={dept}>
                  <td style={{
                    padding: "4px 8px", fontWeight: 600, color: "#333",
                    fontSize: 11, whiteSpace: "nowrap", maxWidth: 140,
                    overflow: "hidden", textOverflow: "ellipsis",
                  }} title={dept}>{dept}</td>

                  {MONTHS.map((m) => {
                    const cell = row[m];
                    const pct  = cell?.pct;
                    return (
                      <td key={m}
                        style={{
                          background: getColor(pct), borderRadius: 5,
                          textAlign: "center", color: getTextColor(pct),
                          padding: "5px 2px", cursor: cell ? "pointer" : "default",
                          fontWeight: pct > 90 ? 700 : 400, fontSize: 10,
                          transition: "transform 0.1s",
                        }}
                        title={cell ? `${dept} – ${m}\nSpend: ₹${Number(cell.spend).toLocaleString("en-IN")}\nBudget: ₹${Number(cell.budget).toLocaleString("en-IN")}\nUtilization: ${pct}%` : "No data"}
                        onMouseEnter={(e) => cell && setTooltip({ dept, m, cell, x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        {pct !== undefined ? `${pct}%` : "–"}
                      </td>
                    );
                  })}

                  {/* Avg column */}
                  <td style={{
                    background: getColor(parseFloat(avg)), borderRadius: 5,
                    textAlign: "center", fontWeight: 700,
                    color: getTextColor(parseFloat(avg)), fontSize: 10, padding: "5px 2px",
                  }}>
                    {avg !== null ? `${avg}%` : "–"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Floating tooltip */}
        {tooltip && (
          <div style={{
            position: "fixed", top: tooltip.y + 12, left: tooltip.x + 12,
            background: "#1a2340", color: "#fff", borderRadius: 8,
            padding: "10px 14px", fontSize: 12, zIndex: 9999,
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)", pointerEvents: "none",
            lineHeight: 1.7,
          }}>
            <strong>{tooltip.dept} – {tooltip.m}</strong><br />
            Spend: ₹{Number(tooltip.cell.spend).toLocaleString("en-IN")}<br />
            Budget: ₹{Number(tooltip.cell.budget).toLocaleString("en-IN")}<br />
            Utilization: <strong style={{ color: getColor(tooltip.cell.pct) === "#2ecc71" ? "#2ecc71" : "#f39c12" }}>
              {tooltip.cell.pct}%
            </strong>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
        {LEGEND.map(({ label, color }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: "#666" }}>
            <div style={{ width: 14, height: 14, borderRadius: 3, background: color }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
}
